import SEO from '@cannect/components/SEO'
import {
  getAppAvailability,
  getPathologiesForSessionCall,
  getPathologiesOptions
} from '@cannect/services/resources/instantConsultation/requests'
import { InstantConsultationProvider, useInstantConsultation } from 'hooks/useInstantConsultation'
import {
  getScreeningNext,
  SCREENING_TOKEN_KEY
} from '@cannect/services/resources/screening/requests'
import { useBrand } from 'hooks/useBrand'
import { useTokenApi } from 'hooks/useTokenApi'
import { CalendarClock, Loader2 } from 'lucide-react'
import { ReactNode, useEffect, useRef, useState } from 'react'
import { Redirect, useHistory, useLocation } from 'react-router-dom'
import 'styles/aspaec-theme.css'
import 'styles/remederi-theme.css'
import 'styles/vikura-theme.css'
import {
  InstantConsultationResumeHandler,
  InstantConsultationLocationState,
  getResumeSessionIdFromSearch
} from './InstantConsultationResumeHandler'
import { ConclusionStep } from './steps/ConclusionStep'
import { IdentificationStep } from './steps/IdentificationStep'
import { PaymentStep } from './steps/PaymentStep'
import { QuestionnaireStep } from './steps/QuestionnaireStep'
import { QueueStep } from './steps/QueueStep'
import { TriageStep } from './steps/TriageStep'
import { VideoCallStep } from './steps/VideoCallStep'

const STEP_LABELS: Record<string, string> = {
  triage: 'Triagem',
  questionnaire: 'Questionário',
  payment: 'Pagamento',
  queue: 'Fila de espera',
  video: 'Consulta',
  conclusion: 'Conclusão'
}

const VISIBLE_STEPS = ['triage', 'questionnaire', 'payment', 'queue', 'video', 'conclusion']

function ScreeningSkipBootstrap() {
  const { handleCreateSession, goTo, setQuestionnaireCompleted, setSelectedPathologies } = useInstantConsultation()
  const ran = useRef(false)

  useEffect(() => {
    if (ran.current) return
    const token = new URLSearchParams(window.location.search).get('screening')
    if (!token) return
    sessionStorage.setItem(SCREENING_TOKEN_KEY, token)
    ran.current = true
    ;(async () => {
      try {
        const next = await getScreeningNext(token)
        const [allowed, scheduling] = await Promise.all([getPathologiesForSessionCall(), getPathologiesOptions()])
        const names = next.filter_names || []
        const merged = [...allowed, ...(scheduling.pathologies || [])]
        const selected = merged.filter((item) => names.includes(item.name))
        setSelectedPathologies(selected.length ? selected : allowed.slice(0, 1))
        setQuestionnaireCompleted(true)
        await handleCreateSession()
        goTo('payment')
      } catch {
        goTo('payment')
      }
    })()
  }, [goTo, handleCreateSession, setQuestionnaireCompleted, setSelectedPathologies])

  return (
    <div className="flex flex-1 items-center justify-center py-16 text-sm text-gray-500">
      Preparando sua consulta…
    </div>
  )
}

function StepIndicator() {
  const { step } = useInstantConsultation()
  const currentIdx = VISIBLE_STEPS.indexOf(step)

  if (step === 'video') return null

  return (
    <div className="border-b px-4 py-3">
      <div className="mx-auto mt-7 flex max-w-2xl items-center justify-center">
        {VISIBLE_STEPS.map((s, idx) => {
          let circleClass = 'border-2 border-gray-200 text-gray-300'
          if (idx < currentIdx) circleClass = 'bg-primary-700 text-white'
          else if (idx === currentIdx) circleClass = 'border-2 border-primary-700 text-primary-700'

          return (
            <div key={s} className="flex items-center">
              <div
                className={`flex h-7 w-7 items-center justify-center rounded-full text-xs font-semibold transition-colors ${circleClass}`}>
                {idx < currentIdx ? '✓' : idx + 1}
              </div>
              {idx < VISIBLE_STEPS.length - 1 && (
                <div
                  className={`h-0.5 w-6 transition-colors md:w-15 ${idx < currentIdx ? 'bg-primary-700' : 'bg-gray-200'}`}
                />
              )}
            </div>
          )
        })}
      </div>
      <p className="mt-1 text-center text-xs text-gray-500">{STEP_LABELS[step]}</p>
    </div>
  )
}

function InstantConsultationContent({
  linkResumeSessionId,
  skipLegacyTriage
}: {
  linkResumeSessionId: number | null
  skipLegacyTriage: boolean
}) {
  const { step } = useInstantConsultation()
  const { config } = useBrand()

  const renderStep = () => {
    if (skipLegacyTriage && (step === 'triage' || step === 'questionnaire')) {
      return <ScreeningSkipBootstrap />
    }

    switch (step) {
      case 'triage':
        return <TriageStep />
      case 'questionnaire':
        return <QuestionnaireStep />
      case 'payment':
        return <PaymentStep />
      case 'queue':
        return <QueueStep />
      case 'video':
        return <VideoCallStep />
      case 'conclusion':
        return <ConclusionStep />
      default:
        return <TriageStep />
    }
  }

  return (
    <div className="flex min-h-screen flex-col bg-gray-50">
      <InstantConsultationResumeHandler linkResumeSessionId={linkResumeSessionId} />
      <SEO
        title={`Consulta na Hora ${config.name !== 'Cannect' ? config.name : ''}`.trim()}
        description="Conecte-se agora com um especialista em cannabis medicinal para uma consulta imediata."
        canonical={`${config.pathPrefix ?? ''}/consulta-na-hora`}
      />

      <StepIndicator />

      <main className={`flex min-h-0 flex-1 flex-col ${step === 'video' ? '' : 'overflow-y-auto'}`}>
        {renderStep()}
      </main>
    </div>
  )
}

const DEFAULT_UNAVAILABLE_MESSAGE =
  'No momento não há médicos disponíveis para atendimento imediato. Tente novamente mais tarde.'

function AvailabilityGate({ children, hasLinkResume }: { children: ReactNode; hasLinkResume: boolean }) {
  const location = useLocation<InstantConsultationLocationState>()
  const history = useHistory()
  // Retomada de uma sessão em andamento (US4) ignora o gate de disponibilidade —
  // tanto por navegação interna quanto pelo link compartilhável (?session=).
  // Guardado em ref: o handler limpa a query após hidratar, e sem isso o gate
  // voltaria a exigir disponibilidade num re-render e derrubaria a retomada.
  const resumeSeenRef = useRef(Boolean(location.state?.resumeSessionId) || hasLinkResume)
  const hasResume = resumeSeenRef.current

  const [checking, setChecking] = useState(!hasResume)
  const [available, setAvailable] = useState(hasResume)
  const [message, setMessage] = useState<string | null>(null)

  useEffect(() => {
    if (hasResume) return undefined
    let active = true
    ;(async () => {
      try {
        const res = await getAppAvailability()
        if (!active) return
        const isAvailable = Boolean(res?.availability?.is_available)
        setAvailable(isAvailable)
        setMessage(res?.availability?.message ?? null)
      } catch {
        if (!active) return
        setAvailable(false)
        setMessage(null)
      } finally {
        if (active) setChecking(false)
      }
    })()
    return () => {
      active = false
    }
  }, [hasResume])

  if (checking) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-3 bg-gray-50 text-gray-500">
        <Loader2 className="h-8 w-8 animate-spin text-primary-700" />
        <p className="text-sm">Verificando disponibilidade...</p>
      </div>
    )
  }

  if (!available) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-4 bg-gray-50 px-4 text-center">
        <SEO title="Consulta na Hora" description="Atendimento imediato indisponível." canonical="/consulta-na-hora" />
        <div
          className="flex h-16 w-16 items-center justify-center rounded-full"
          style={{ backgroundColor: 'var(--app-green-soft, rgba(33, 151, 110, 0.12))' }}>
          <CalendarClock className="h-8 w-8 text-primary-700" />
        </div>
        <h1 className="font-spartan text-2xl font-bold text-gray-800">Atendimento indisponível</h1>
        <p className="max-w-md text-sm text-gray-500">{message || DEFAULT_UNAVAILABLE_MESSAGE}</p>
        <button
          type="button"
          onClick={() => history.push('/')}
          className="mt-2 rounded-lg bg-primary-700 px-6 py-3 text-sm font-semibold text-white transition-colors hover:bg-primary-800">
          Voltar para o início
        </button>
      </div>
    )
  }

  // eslint-disable-next-line react/jsx-no-useless-fragment -- children é ReactNode; fragmento garante retorno JSX.Element
  return <>{children}</>
}

export default function InstantConsultation() {
  const { brand, config } = useBrand()
  const { tokenApi } = useTokenApi()
  const { state, search } = useLocation<{ fromSchedulingIdentification?: boolean }>()
  // Whitelabel: dados do paciente SEMPRE são pedidos ao entrar na página,
  // mesmo com sessão ativa no navegador (mesma regra do agendamento branded) —
  // o register-or-retrieve resolve o user e renova o token. Exceção: quem chega
  // pelo "Consultar agora" do agendamento acabou de preencher os dados lá —
  // reaproveita a identificação em vez de pedir de novo.
  const [identifiedInThisVisit, setIdentifiedInThisVisit] = useState(Boolean(state?.fromSchedulingIdentification))
  // O id do link (?session=) é capturado no primeiro render e mantido em ref:
  // no whitelabel a identificação vem antes do handler montar, e o handler
  // limpa a query depois — ler a URL mais tarde devolveria vazio.
  const linkResumeIdRef = useRef(getResumeSessionIdFromSearch(search))
  // Whitelabel (Vikura/Remederi): themeClass recolore os utilitários primary-*
  // da página inteira, mesmo padrão do agendamento branded.
  const themeClass = config.scheduling.themeClass ?? ''
  const isWhitelabel = brand !== 'cannect'
  const screeningToken = new URLSearchParams(search).get('screening')

  // Marcas sem Consulta na Hora (ex.: Aspaec) não acessam este fluxo
  if (isWhitelabel && config.scheduling.hasInstantConsultation !== true) {
    return <Redirect to="/" />
  }

  // Whitelabel é "dados primeiro" e SEM tela de login: o paciente informa os
  // dados (register-or-retrieve autentica de forma transparente) antes da
  // triagem — em toda visita, mesmo já tendo token salvo. Cannect mantém o
  // comportamento atual (login só no fim da triagem).
  if ((isWhitelabel || screeningToken) && (!identifiedInThisVisit || !tokenApi)) {
    return (
      <div className={themeClass}>
        <SEO
          title={`Consulta na Hora ${config.name}`}
          description="Conecte-se agora com um especialista para uma consulta imediata."
          canonical={`${config.pathPrefix ?? ''}/consulta-na-hora`}
        />
        <div className="flex min-h-screen flex-col bg-gray-50">
          <IdentificationStep onComplete={() => setIdentifiedInThisVisit(true)} />
        </div>
      </div>
    )
  }

  return (
    <div className={themeClass}>
      <InstantConsultationProvider>
        <AvailabilityGate hasLinkResume={linkResumeIdRef.current !== null}>
          <InstantConsultationContent
            linkResumeSessionId={linkResumeIdRef.current}
            skipLegacyTriage={Boolean(screeningToken)}
          />
        </AvailabilityGate>
      </InstantConsultationProvider>
    </div>
  )
}
